/**
 * The one entry point to the guided tours.
 *
 * Clicking runs the tour for the page you are on. The menu behind it lists
 * every tour, so someone can read about a page before going there — and reset
 * the "already seen" marks, which is what a manager training a new cashier on
 * their own laptop actually needs.
 */

import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { HelpCircle, RotateCcw, Play } from 'lucide-react';
import { useCanAccess } from '@/components/auth/ProtectedRoute';
import { ALL_TOURS, tourForPath } from '@/lib/tours/tours';
import { runTour, tourSeen, resetAllTours } from '@/lib/tours/runTour';
import { cn } from '@/lib/format';

const PATH_FOR_TOUR: Record<string, string> = {
  dashboard: '/dashboard',
  pos: '/pos',
  'pos-cart': '/pos/cart',
  inventory: '/inventory',
  events: '/events',
  'event-stock': '/events',
  'event-detail': '/events',
  customers: '/customers',
  transactions: '/transactions',
  returns: '/returns',
  piutang: '/piutang',
  reports: '/reports',
  settings: '/settings',
  users: '/users',
  'audit-logs': '/audit-logs',
};

export function HelpButton() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const canAccess = useCanAccess();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current = tourForPath(pathname);
  // Tours for pages this role cannot open would only lead to a 403; the same
  // list the sidebar reads decides which ones to offer.
  const visible = ALL_TOURS.filter((t) => canAccess(PATH_FOR_TOUR[t.id] ?? '/dashboard'));

  // First visit to a page starts its tour on its own. Waiting a beat lets the
  // page finish its first render, or the steps would be filtered against a
  // DOM that has not painted its table yet.
  useEffect(() => {
    if (!current || tourSeen(current.id)) return;
    const t = setTimeout(() => void runTour(current), 900);
    return () => clearTimeout(t);
  }, [current]);

  // Close on click, not mousedown — mousedown would eat the click that
  // dismissed it. Same reason as the customer picker.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, [open]);

  const start = (id: string) => {
    setOpen(false);
    const tour = ALL_TOURS.find((t) => t.id === id);
    if (!tour) return;
    const target = PATH_FOR_TOUR[id];
    if (target && !pathname.startsWith(target)) {
      // Navigate first; the steps point at that page's elements.
      navigate(target);
      setTimeout(() => void runTour(tour), 700);
      return;
    }
    void runTour(tour);
  };

  return (
    <div ref={ref} className="relative">
      {/* One button, one menu. An earlier version put a second 16px button in
          the corner for the list, which is exactly the too-small tap target
          this app has already been bitten by. */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-9 h-9 inline-flex items-center justify-center rounded-lg text-ink-subtle hover:text-ink hover:bg-canvas transition-colors focus:outline-none focus:ring-2 focus:ring-primary/40"
        title="Tutorial"
        aria-label="Buka menu tutorial"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <HelpCircle size={18} />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-64 z-40 bg-surface border border-line rounded-lg shadow-hover py-1.5 overflow-hidden"
        >
          {current && (
            <>
              <button
                type="button"
                role="menuitem"
                onClick={() => start(current.id)}
                className="w-full text-left px-3 py-2.5 hover:bg-canvas flex items-center gap-2"
              >
                <Play size={13} className="shrink-0 text-primary" />
                <span className="min-w-0">
                  <span className="block text-small font-medium text-ink">
                    Tutorial halaman ini
                  </span>
                  <span className="block text-caption text-ink-subtle truncate">
                    {current.title}
                  </span>
                </span>
              </button>
              <div className="border-t border-line my-1" />
            </>
          )}
          <p className="px-3 py-1.5 text-overline uppercase tracking-wider text-ink-subtle font-medium">
            Semua halaman
          </p>
          <div className="max-h-80 overflow-y-auto">
            {visible.map((t) => (
              <button
                key={t.id}
                type="button"
                role="menuitem"
                onClick={() => start(t.id)}
                className={cn(
                  'w-full text-left px-3 py-2 text-small hover:bg-canvas flex items-center gap-2',
                  current?.id === t.id ? 'text-primary font-medium' : 'text-ink',
                )}
              >
                <Play size={12} className="shrink-0 opacity-60" />
                {t.title}
                {!tourSeen(t.id) && (
                  <span className="ml-auto w-1.5 h-1.5 rounded-full bg-primary" title="Belum dibuka" />
                )}
              </button>
            ))}
          </div>
          <div className="border-t border-line mt-1 pt-1">
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                resetAllTours();
                setOpen(false);
              }}
              className="w-full text-left px-3 py-2 text-small text-ink-subtle hover:bg-canvas hover:text-ink flex items-center gap-2"
            >
              <RotateCcw size={12} />
              Tampilkan lagi semua tutorial
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
